// GauntletJob: read-only view of a single gauntlet run. Polls
// /api/gauntlet/:id every 3s while the run is in flight, then stops.
//
// Visual language matches DeckDetail: app design tokens (`surface`,
// `text-fg-muted`, `font-display`, `eyebrow`), `ProvenanceLine` for
// metadata, `Markdown` for prose so `{Card Name}` references render
// as `CardHover` popovers. The structured editor's brief
// (matchup_verdicts, weakness_themes, swap_candidates) renders as
// dedicated UI rather than a JSON dump.

import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Swords } from "lucide-react";
import { getGauntlet, summarizeGauntlet, type GauntletJob } from "../lib/api";
import LiquidButton from "../components/ui/LiquidButton";
import { ACTION_COSTS } from "../lib/credits";
import Markdown from "../components/Markdown";
import CardHover from "../components/CardHover";
import ProvenanceLine from "../components/ProvenanceLine";
import StatusDot, { type Status } from "../components/StatusDot";
import { winrateTone } from "../lib/winrate";
import { cn } from "../lib/cn";

const META_OWNER_USERNAME = "_system_meta";

/** Mirror of GauntletEditorsBrief from src/bedrock/summarize-gauntlet.ts.
 *  Lives client-side as `unknown` on the wire; we narrow defensively
 *  so a malformed payload doesn't crash the page. */
interface MatchupVerdict {
  archetype: string;
  record: string;
  verdict: "favored" | "even" | "unfavored";
  notes: string;
}
interface SwapCandidate {
  cut: string | null;
  add: string | null;
  rationale: string;
}
interface EditorsBrief {
  overall_winrate: number;
  matchup_verdicts: MatchupVerdict[];
  weakness_themes: string[];
  swap_candidates: SwapCandidate[];
}

function asEditorsBrief(json: Record<string, unknown> | null): EditorsBrief | null {
  if (!json) return null;
  const verdicts = Array.isArray(json.matchup_verdicts) ? json.matchup_verdicts : null;
  const themes = Array.isArray(json.weakness_themes) ? json.weakness_themes : null;
  const swaps = Array.isArray(json.swap_candidates) ? json.swap_candidates : null;
  if (!verdicts || !themes || !swaps) return null;
  return {
    overall_winrate: Number(json.overall_winrate ?? 0),
    matchup_verdicts: verdicts as MatchupVerdict[],
    weakness_themes: themes as string[],
    swap_candidates: swaps as SwapCandidate[],
  };
}

/** Strip the `## Prose summary` heading that the v1 prompt prepends.
 *  The Markdown component renders headings as plain paragraphs, so we
 *  drop the leading marker line to avoid `## Prose summary` showing
 *  up as literal text. */
function stripProseHeader(text: string): string {
  return text.replace(/^\s*##\s*Prose summary\s*\n+/i, "").trim();
}

export default function GauntletJobPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ["gauntlet", id],
    queryFn: () => getGauntlet(id!),
    enabled: Boolean(id),
    // Poll while in flight; stop once the run reaches a terminal
    // status. Derived from the data so we don't keep a sibling state
    // bool in sync via useEffect.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "complete" || s === "error" ? false : 3000;
    },
  });

  const job = query.data;
  const brief = useMemo(
    () => asEditorsBrief(job?.rollup_summary_json ?? null),
    [job?.rollup_summary_json],
  );

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <div className="skeleton h-12 w-1/2" />
        <div className="skeleton h-32 w-full" />
        <div className="skeleton h-48 w-full" />
      </div>
    );
  }
  if (query.error || !job) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="surface p-4 text-sm text-danger">
          Failed to load gauntlet: {(query.error as Error)?.message ?? "unknown"}
        </div>
      </div>
    );
  }

  const winrate = job.winrate !== null ? parseFloat(job.winrate) : null;
  const inFlight = job.status === "running" || job.status === "summarizing" || job.status === "queued";
  const completedAt = job.completed_at;
  const elapsedMs = completedAt
    ? new Date(completedAt).getTime() - new Date(job.started_at).getTime()
    : null;

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      <header className="space-y-4">
        <div className="eyebrow flex items-center gap-2 flex-wrap">
          <Swords size={12} strokeWidth={1.75} />
          <span>Gauntlet</span>
          <StatusDot status={statusTone(job.status)} label={job.status.toUpperCase()} />
          {inFlight && job.phase && (
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
              · {humanizePhase(job.phase)}
            </span>
          )}
          {job.user_deck_slug && job.user_deck_theme && job.user_deck_owner_username && (
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
              ·{" "}
              <span className="text-fg-subtle">for </span>
              <Link
                to={`/decks/${encodeURIComponent(job.user_deck_owner_username)}/${encodeURIComponent(job.user_deck_slug)}`}
                className="text-accent hover:underline normal-case tracking-normal"
              >
                {job.user_deck_theme}
              </Link>
            </span>
          )}
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
          vs top-meta gauntlet
        </h1>
        {job.error && (
          <div className="surface p-4 text-sm text-danger">{job.error}</div>
        )}
        <ProvenanceLine
          fields={[
            { value: new Date(job.started_at).toLocaleString() },
            ...(elapsedMs !== null
              ? [{ label: "took", value: `${(elapsedMs / 1000).toFixed(1)}s` }]
              : []),
            { label: "games", value: `${job.games_per_archetype} per archetype` },
            { label: "id", value: job.id.slice(0, 12) + "…" },
          ]}
        />
      </header>

      <WinrateBlock
        winrate={winrate}
        userWins={job.outcome.total_user_wins}
        totalGames={job.outcome.total_games}
        inFlight={inFlight}
      />

      <section className="space-y-3">
        <header className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="flex items-baseline gap-3">
            <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
              Per-archetype matchups
            </h2>
            {inFlight &&
              (() => {
                const label = matchProgressLabel(
                  job.outcome.per_archetype,
                  job.games_per_archetype,
                );
                if (!label) return null;
                return (
                  <span className="rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-accent whitespace-nowrap">
                    {label}
                  </span>
                );
              })()}
          </div>
          {inFlight && (
            <MatchupPips
              perArchetype={job.outcome.per_archetype}
              gamesPerArchetype={job.games_per_archetype}
            />
          )}
        </header>
        <div className="grid gap-3 sm:grid-cols-3">
          {job.outcome.per_archetype.map((m, i) => (
            <ArchetypeCard
              key={m.archetype_deck_id}
              outcome={m}
              verdict={brief?.matchup_verdicts[i]}
              gamesPerArchetype={job.games_per_archetype}
              state={deriveMatchupState(m, job.games_per_archetype, inFlight)}
            />
          ))}
        </div>
      </section>

      {job.status === "complete" && !job.rollup_summary && (
        <SummarizeCta gauntletId={job.id} />
      )}

      {job.rollup_summary && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
            Gauntlet summary
          </h2>
          <div className="surface p-5 space-y-3 text-sm leading-relaxed">
            <Markdown text={stripProseHeader(job.rollup_summary)} />
          </div>
        </section>
      )}

      {brief && brief.weakness_themes.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
            Weakness themes
          </h2>
          <div className="flex flex-wrap gap-2">
            {brief.weakness_themes.map((t) => (
              <span
                key={t}
                className="rounded-full border border-border bg-bg-raised/60 px-2.5 py-0.5 font-mono text-[11px] text-fg-muted"
              >
                {t}
              </span>
            ))}
          </div>
        </section>
      )}

      {brief && brief.swap_candidates.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
            Swap candidates
          </h2>
          <ul className="surface divide-y divide-border">
            {brief.swap_candidates.map((s, i) => (
              <SwapRow key={i} swap={s} />
            ))}
          </ul>
        </section>
      )}

      {inFlight && !job.rollup_summary && (
        <div className="surface p-4 text-sm text-fg-muted">
          Gauntlet is still running — the summary lands once the third matchup
          completes.
        </div>
      )}
    </div>
  );
}

function statusTone(status: GauntletJob["status"]): Status {
  if (status === "complete") return "success";
  if (status === "error") return "error";
  return "active";
}

function humanizePhase(phase: string): string {
  // Phase strings come from gauntlet-runner.ts as snake_case identifiers
  // ("playing_matchups", "rolling_up"). Render with spaces.
  return phase.replace(/_/g, " ");
}

/** Per-archetype card lifecycle. Matchups run sequentially in
 *  gauntlet-runner.ts (per-user CPU concurrency cap), so at any
 *  moment at most one card is "playing" — the rest are either
 *  "complete" (game count reached) or "queued" (haven't been
 *  scheduled yet). The page polls every 3s so the active card
 *  ticks up game-by-game; queued cards sit at 0 until the runner
 *  reaches them. */
type MatchupState = "queued" | "playing" | "complete";

function deriveMatchupState(
  outcome: GauntletJob["outcome"]["per_archetype"][number],
  gamesPerArchetype: number,
  inFlight: boolean,
): MatchupState {
  const totalDone = outcome.user_wins + outcome.archetype_wins + outcome.draws + outcome.dnf;
  if (totalDone >= gamesPerArchetype) return "complete";
  // The card with any games_played > 0 (but not yet at gamesPerArchetype)
  // is the active matchup. Cards still at 0 are queued behind it.
  if (inFlight && totalDone > 0) return "playing";
  return "queued";
}

/** Index of the currently-running matchup (1-based) and total count.
 *  Used by the header pill to render "Match 2 of 3" while the gauntlet
 *  is in flight. Falls back to the next-up queued slot when nothing is
 *  technically "playing" yet (gap between matchups while the worker
 *  finalizes the previous and creates the next playtest_jobs row). */
function matchProgressLabel(
  perArchetype: GauntletJob["outcome"]["per_archetype"],
  gamesPerArchetype: number,
): string | null {
  if (perArchetype.length === 0) return null;
  let activeIdx = -1;
  let nextIdx = -1;
  for (let i = 0; i < perArchetype.length; i++) {
    const m = perArchetype[i]!;
    const totalDone = m.user_wins + m.archetype_wins + m.draws + m.dnf;
    if (totalDone > 0 && totalDone < gamesPerArchetype) {
      activeIdx = i;
      break;
    }
    if (totalDone === 0 && nextIdx < 0) nextIdx = i;
  }
  const idx = activeIdx >= 0 ? activeIdx : nextIdx;
  if (idx < 0) return null;
  return `Match ${idx + 1} of ${perArchetype.length}`;
}

function WinrateBlock({
  winrate,
  userWins,
  totalGames,
  inFlight,
}: {
  winrate: number | null;
  userWins: number;
  totalGames: number;
  inFlight: boolean;
}) {
  if (winrate === null && totalGames === 0) {
    return (
      <div className="surface p-5">
        <div className="text-[11px] uppercase tracking-wider text-fg-subtle">
          Winrate
        </div>
        <div className="font-mono text-3xl tabular-nums text-fg-subtle mt-1">
          {inFlight ? "…" : "—"}
        </div>
      </div>
    );
  }
  const pct = winrate !== null ? winrate * 100 : (userWins / Math.max(1, totalGames)) * 100;
  const tone = winrateTone(pct);
  return (
    <div className="surface p-5">
      <div className="flex items-baseline gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-fg-subtle">
            Winrate
          </div>
          <div className={cn("font-mono text-4xl tabular-nums tracking-tight mt-1", tone)}>
            {pct.toFixed(1)}
            <span className="text-fg-subtle text-base ml-1">%</span>
          </div>
        </div>
        <div className="text-xs text-fg-subtle pl-4 border-l border-border self-stretch flex flex-col justify-center">
          <div className="font-mono tabular-nums">
            {userWins}-{totalGames - userWins}
          </div>
          <div>{totalGames} games across 3 matchups</div>
        </div>
      </div>
    </div>
  );
}

function ArchetypeCard({
  outcome,
  verdict,
  gamesPerArchetype,
  state,
}: {
  outcome: GauntletJob["outcome"]["per_archetype"][number];
  verdict: MatchupVerdict | undefined;
  gamesPerArchetype: number;
  state: MatchupState;
}) {
  const verdictTone = verdict
    ? verdict.verdict === "favored"
      ? "border-accent/50 text-accent"
      : verdict.verdict === "unfavored"
        ? "border-danger/50 text-danger"
        : "border-border text-fg-muted"
    : null;
  const totalDone = outcome.user_wins + outcome.archetype_wins + outcome.draws + outcome.dnf;
  const progress = gamesPerArchetype > 0 ? totalDone / gamesPerArchetype : 0;

  const stateClass =
    state === "playing"
      ? "ring-1 ring-accent/40 anim-pulse-ring"
      : state === "queued"
        ? "border-dashed opacity-60"
        : "";

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <Link
          to={`/decks/${META_OWNER_USERNAME}/${outcome.archetype_slug}`}
          className="text-sm font-medium hover:text-accent truncate"
          title={outcome.archetype_theme}
          onClick={(e) => e.stopPropagation()}
        >
          {outcome.archetype_theme}
        </Link>
        {state === "playing" ? (
          <span className="rounded-full border border-accent/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-accent whitespace-nowrap">
            playing
          </span>
        ) : state === "queued" ? (
          <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-fg-subtle whitespace-nowrap">
            queued
          </span>
        ) : verdictTone && verdict ? (
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider whitespace-nowrap",
              verdictTone,
            )}
          >
            {verdict.verdict}
          </span>
        ) : null}
      </div>
      <div className="font-mono tabular-nums text-2xl">
        {outcome.user_wins}-{outcome.archetype_wins}
        {outcome.draws > 0 && (
          <span className="text-fg-subtle">-{outcome.draws}</span>
        )}
        {outcome.dnf > 0 && (
          <span className="text-fg-subtle text-xs ml-2">({outcome.dnf} dnf)</span>
        )}
        {state !== "complete" && (
          <span className="ml-2 text-xs text-fg-subtle">
            {totalDone}/{gamesPerArchetype}
          </span>
        )}
      </div>
      {(state === "playing" || (state === "queued" && totalDone === 0)) && (
        // Determinate progress bar fed by the gauntlet outcome which
        // ticks up every 3s as the worker reports match_progress
        // events. The active card gets a shimmer overlay; queued
        // cards render a flat 0% rail so the layout doesn't jump
        // when they transition to playing.
        <div className="relative h-1 w-full overflow-hidden rounded-full bg-bg-raised">
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-700 ease-out",
              state === "playing" ? "bg-accent" : "bg-border",
            )}
            style={{ width: `${Math.min(100, progress * 100)}%` }}
          />
          {state === "playing" && (
            <div className="anim-shimmer-overlay pointer-events-none absolute inset-0" />
          )}
        </div>
      )}
      {verdict?.notes && (
        <p className="text-xs text-fg-muted leading-relaxed">{verdict.notes}</p>
      )}
      {outcome.playtest_job_id && state === "complete" && (
        <p className="text-[11px] text-fg-subtle">full match log →</p>
      )}
    </>
  );

  // Only the completed matchup is clickable. Queued/playing cards
  // don't have a playtest UUID yet (or have one but no game logs to
  // browse), so making them links would land users on a stub page.
  if (outcome.playtest_job_id && state === "complete") {
    return (
      <Link
        to={`/playtest/jobs/${outcome.playtest_job_id}`}
        className="surface p-4 space-y-2 block hover:bg-bg-raised/40 transition-colors"
        title="Open this playtest"
      >
        {body}
      </Link>
    );
  }
  return <div className={cn("surface p-4 space-y-2", stateClass)}>{body}</div>;
}

/** 3-segment progress strip rendered next to the matchups header
 *  while the gauntlet is in flight. Matchups run sequentially, so at
 *  most one pip pulses (the currently-playing one); pips ahead of it
 *  are dim/queued, pips behind are solid/complete. Reads game state
 *  from each per_archetype entry, same as the cards below. */
function MatchupPips({
  perArchetype,
  gamesPerArchetype,
}: {
  perArchetype: GauntletJob["outcome"]["per_archetype"];
  gamesPerArchetype: number;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {perArchetype.map((m) => {
        const totalDone = m.user_wins + m.archetype_wins + m.draws + m.dnf;
        const complete = totalDone >= gamesPerArchetype;
        const playing = !complete && totalDone > 0;
        return (
          <span
            key={m.archetype_deck_id}
            className={cn(
              "h-1.5 w-6 rounded-full transition-colors",
              complete
                ? "bg-fg-muted"
                : playing
                  ? "bg-accent anim-pulse-ring"
                  : "bg-bg-raised border border-border",
            )}
            title={`${m.archetype_theme}: ${totalDone}/${gamesPerArchetype}`}
          />
        );
      })}
    </div>
  );
}

function SwapRow({ swap }: { swap: SwapCandidate }) {
  return (
    <li className="px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {swap.cut && (
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">cut</span>
            <CardHover name={swap.cut} className="text-fg">
              {swap.cut}
            </CardHover>
          </span>
        )}
        {swap.cut && swap.add && (
          <span aria-hidden className="text-fg-subtle">→</span>
        )}
        {swap.add && (
          <span className="inline-flex items-baseline gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">add</span>
            <CardHover name={swap.add} className="text-fg">
              {swap.add}
            </CardHover>
          </span>
        )}
      </div>
      {swap.rationale && (
        <p className="mt-1 text-xs text-fg-muted leading-relaxed">{swap.rationale}</p>
      )}
    </li>
  );
}

function SummarizeCta({ gauntletId }: { gauntletId: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => summarizeGauntlet(gauntletId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gauntlet", gauntletId] });
    },
  });
  const errMsg = mutation.error ? (mutation.error as Error).message : null;
  return (
    <section className="surface p-5 space-y-4">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Gauntlet summary
        </h2>
        <p className="text-sm text-fg-muted leading-relaxed">
          The matches are done. Generate a gauntlet summary — recap, weakness
          themes, and card-swap suggestions tailored to how each matchup
          played out.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <LiquidButton
          className="h-10 text-sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          trailing={
            <span className="text-[10px] font-mono opacity-70">
              · {ACTION_COSTS.gauntlet_summary}
            </span>
          }
        >
          <Sparkles size={14} strokeWidth={2} />
          {mutation.isPending ? "Generating summary…" : "Generate summary"}
        </LiquidButton>
        {errMsg && (
          <span className="text-xs text-danger" role="alert">
            {errMsg}
          </span>
        )}
      </div>
    </section>
  );
}
